import { Controller } from "@hotwired/stimulus"
import { post } from '@rails/request.js'
import jquery from "jquery";
require("dragula/dist/dragula.min.css")

import dragula from 'dragula';

export default class extends Controller {
  static values = {
    reorderPath: String,
    handleClass: { type: String, default: 'reorder-handle' },
    undraggableClass: { type: String, default: 'undraggable' },
    saveOnReorder: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false }
  }

  // will be reissued as native dom events name prepended with 'sortable:' e.g. 'sortable:drag', 'sortable:drop', etc
  static pluginEventsToReissue = [ "drag", "dragend", "drop", "cancel", "remove", "shadow", "over", "out", "cloned" ]

  initialize() {
    if (window.jQuery === undefined) {
      window.jQuery = jquery // required for select2 used for time zone select, but we also use global jQuery throughout below
    }
    this.lastTap = 0
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    this.isAndroid = /Android/i.test(navigator.userAgent)
    this.lastTapTarget = null
    this.isDoubleTapping = false
    this.doubleTapDelay = 500 // More forgiving delay
    this.touchStartX = 0
    this.touchStartY = 0
    this.handleKeyDown = this.handleKeyDown.bind(this)
  }

  connect() {
    if (!this.hasReorderPathValue) { return }
    this.initPluginInstance()
    if (this.isMobile) {
      this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false })
      this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false })
      this.element.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false })
    }
    document.addEventListener('keydown', this.handleKeyDown)
  }

  disconnect() {
    this.teardownPluginInstance()
    if (this.isMobile) {
      this.element.removeEventListener('touchstart', this.handleTouchStart.bind(this))
      this.element.removeEventListener('touchend', this.handleTouchEnd.bind(this))
      this.element.removeEventListener('touchmove', this.handleTouchMove.bind(this))
    }
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && this.plugin) {
      this.plugin.cancel()
    }
  }

  isDoubleTap(currentTime) {
    const tapLength = currentTime - this.lastTap
    return tapLength > 0 && tapLength < this.doubleTapDelay
  }

  handleTouchStart(event) {
    // Store the touch start position
    if (event.touches.length === 1) {
      this.touchStartX = event.touches[0].clientX
      this.touchStartY = event.touches[0].clientY
    }

    const currentTime = new Date().getTime()

    if (this.isDoubleTap(currentTime)) {
      // This is a double-tap, prevent default behavior including text selection
      event.preventDefault()
      this.isDoubleTapping = true
    }

    this.lastTap = currentTime
    this.lastTapTarget = event.target
  }

  handleTouchMove(event) {
    if (!this.isDoubleTapping) return

    // Calculate movement distance
    const deltaX = event.touches[0].clientX - this.touchStartX
    const deltaY = event.touches[0].clientY - this.touchStartY

    // If movement is detected during double tap, prevent it from being a scroll
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      event.preventDefault()
    }
  }

  handleTouchEnd(event) {
    if (this.isDoubleTapping) {
      event.preventDefault()
      // For Android, we need a small delay before starting the drag
      if (this.isAndroid) {
        setTimeout(() => {
          this.isDoubleTapping = false
        }, 50)
      }
    }
  }

  isRelatedTarget(element1, element2) {
    if (!element1 || !element2) return false

    // Get the closest draggable parent for both elements
    const draggable1 = element1.closest('[data-id]') || element1
    const draggable2 = element2.closest('[data-id]') || element2

    // Check if they're in the same draggable area
    return draggable1 === draggable2 || draggable1.contains(draggable2) || draggable2.contains(draggable1)
  }

  initPluginInstance() {
    const self = this
    const reorderHandleSelector = '.' + this.handleClassValue
    this.plugin = dragula([this.element], {
      moves: function(el, container, handle) {
        if (self.disabledValue) return false

        var $handles = jQuery(el).find(reorderHandleSelector)

        // Check for double tap on mobile
        if (self.isMobile) {
          // More lenient double-tap check that relies on isDoubleTapping state
          if (self.isDoubleTapping) {
            // Reset the state after allowing the drag
            if (!self.isAndroid) {
              self.isDoubleTapping = false
            }

            // Proceed with normal drag check
            if ($handles.length) {
              return !!jQuery(handle).closest(reorderHandleSelector).length
            } else {
              if (!jQuery(handle).closest(reorderHandleSelector).length) {
                return self.element === container
              } else {
                return false
              }
            }
          }
          return false
        } else {
          // On desktop, proceed with normal drag check
          if ($handles.length) {
            return !!jQuery(handle).closest(reorderHandleSelector).length
          } else {
            if (!jQuery(handle).closest(reorderHandleSelector).length) {
              return self.element === container
            } else {
              return false
            }
          }
        }
      },
      accepts: function (el, target, source, sibling) {
        if (jQuery(sibling).hasClass(this.reorderUndraggableClassValue) && jQuery(sibling).prev().hasClass(this.reorderUndraggableClassValue)) {
          return false
        } else {
          return true
        }
      },
    }).on('drop', function (el) {
      // save order here.
      if (self.saveOnReorderValue) {
        self.saveSortOrder()
      }
    }).on('over', function (el, container) {
      // deselect any text fields, or else things go slow!
      jQuery(document.activeElement).blur()
    })

    this.initReissuePluginEventsAsNativeEvents()
  }

  initReissuePluginEventsAsNativeEvents() {
    this.constructor.pluginEventsToReissue.forEach((eventName) => {
      this.plugin.on(eventName, (...args) => {
        this.dispatch(eventName, { detail: { plugin: 'dragula', type: eventName, args: args }})
      })
    })
  }

  teardownPluginInstance() {
    if (this.plugin === undefined) { return }

    // revert to original markup, remove any event listeners
    this.plugin.destroy()
  }

  saveSortOrder() {
    var idsInOrder = Array.from(this.element.childNodes).map((el) => { return parseInt(el.dataset?.id) });

    post(this.reorderPathValue, { body: JSON.stringify({ids_in_order: idsInOrder}) })
  }
}
